import PropTypes from 'prop-types';
import Hooks from 'core/presentation/hooks';
import UIViewUtils from './utils';
import MojitoNGen from 'mojito/ngen';
import { routingProps } from 'core/constants/routing-props.js';

const log = MojitoNGen.logger.get('UIView');

/**
 * UIView is a High Order Component (HoC) function used to wrap the component implementation
 * with basic Mojito functionality and tooling useful for each view.
 *
 * UIView will look for the component implementation by its name, if nothing is found,
 * the default implementation from function arguments will be used.
 *
 * As a result, it will return a new functional component that renders implementation
 * and passes Mojito helpers and config objects through props.
 *
 * @function UIView
 * @memberof Mojito.Core.Presentation
 *
 * @param {string} name - The name of the view.
 * @param {object} defaultImpl - The default implementation of the class.
 * @param {object} [propTypes] - The prop types of the view.
 * @param {object} [defaultProps = {}] - The default prop values of the view.
 *
 * @returns {Function} MojitoComponent function.
 */
export default function UIView(name, defaultImpl, propTypes, defaultProps) {
    const mojitoPropTypes = {
        ...routingProps,
        ...propTypes,
        config: PropTypes.object,
    };

    const MojitoComponent = props => {
        let componentProps = UIViewUtils.ensureDefaultProps(props, defaultProps);
        componentProps = UIViewUtils.sanitizeRouteProps(componentProps);
        const appContext = Hooks.useAppContext();
        const Implementation =
            Hooks.useImplementation(name, appContext.uiContextPath) || defaultImpl;

        const mojitoTools = Hooks.useComponentTools(
            name,
            props.config,
            Implementation,
            appContext,
            Hooks.useEmitPerformanceMark()
        );
        Hooks.usePropTypesValidation(componentProps, mojitoPropTypes, name);

        if (!Implementation) {
            log.error(`No implementation found for ${name}.`);
            return null;
        }

        return <Implementation {...componentProps} mojitoTools={mojitoTools} />;
    };

    MojitoComponent.propTypes = mojitoPropTypes;
    MojitoComponent.displayName = `${name}UIViewWrapper`;

    return MojitoComponent;
}
